import { Image, utils, Webcam as BnbWebcam } from "@banuba/webar"
import { debounce } from "./lodash"
import * as Logger from "./logger"

class Webcam extends BnbWebcam {
  private readonly _debouncedStop = debounce(() => {
    Logger.debug("Webcam.stop()")
    this.stop()
  }, 250)

  cancelStop = () => {
    this._debouncedStop.cancel()
    Logger.debug("Webcam.scheduleStop() canceled")
  }

  scheduleStop = () => {
    Logger.debug("Webcam.scheduleStop()")
    this._debouncedStop()
  }

  async takePhoto() {
    const video = await utils.createVideoElement(await this["_stream"])
    const minDim = Math.min(video.videoWidth, video.videoHeight)
    const canvas = document.createElement("canvas")
    canvas.width = minDim
    canvas.height = minDim
    const sx = (video.videoWidth - minDim) / 2
    const sy = (video.videoHeight - minDim) / 2
    const ctx = canvas.getContext("2d")!
    ctx.translate(canvas.width, 0)
    ctx.scale(-1, 1)
    ctx.drawImage(video, sx, sy, minDim, minDim, 0, 0, canvas.width, canvas.height)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    video.src = ""
    video.srcObject = null
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) =>
        blob ? resolve(blob) : reject(new Error("Unexpected error: Unable to take Webcam photo"))
      )
    )
  }
}

const webcam = new Webcam({
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  facingMode: { ideal: "user" },
  // @ts-expect-error - resizeMode is not in the standard MediaTrackConstraints type but is supported by some browsers
  resizeMode: "none",
})

const images = new WeakMap<Blob, Image>()

export const get = ((key: "webcam" | Blob) => {
  if (key === "webcam") return webcam
  if (!images.has(key)) {
    Logger.debug("Set new Image object key")
    images.set(key, new Image(key))
  }
  return images.get(key)!
}) as {
  (key: "webcam"): Webcam
  (key: Blob): Image
}
